import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getArticleEditsStorageKey } from '../utils';
import { normalizeArticlePaidValue } from '../../../api/NewsQueueApiConstants';

function toTargetMediaValue(value) {
    if (Array.isArray(value)) {
        const first = value.find((x) => x != null && String(x).trim().length > 0);
        return first == null ? '' : String(first).trim();
    }
    return String(value ?? '').trim();
}

function toNullableString(value) {
    if (typeof value !== 'string') return null;
    const next = value.trim();
    return next.length > 0 ? value : null;
}


function toArrayValue(value) {
    if (Array.isArray(value)) {
        return value.map(v => String(v ?? '').trim()).filter(v => v.length > 0);
    }
    return [];
}


export default function useArticleEdits({
    article,
    articleId,
    canEditFields,
    editableFields,
    onPersistEdits,
}) {
    const [savedEdits, setSavedEdits] = useState({});
    const [editingField, setEditingField] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const editingDraftRef = useRef('');

    useEffect(() => {
        if (!articleId) {
            setSavedEdits({});
            setEditingField(null);
            setEditingValue('');
            return;
        }

        try {
            const raw = localStorage.getItem(getArticleEditsStorageKey(articleId));
            if (!raw) {
                setSavedEdits({});
                setEditingField(null);
                setEditingValue('');
                return;
            }

            const parsed = JSON.parse(raw);
            const next = {};
            const originalHeader = String(article?.[editableFields.HEADER] ?? '');
            const originalIngress = String(article?.[editableFields.INGRESS] ?? '');
            const originalBody = String(article?.[editableFields.BODY] ?? '');
            const originalAuthor = String(article?.[editableFields.AUTHOR] ?? '');
            const originalPhotographer = String(article?.[editableFields.PHOTOGRAPHER] ?? '');
            const originalCaption = String(article?.[editableFields.CAPTION] ?? '');
            const originalImageData = toNullableString(article?.[editableFields.IMAGE_DATA]);
            const originalSection = String(article?.[editableFields.SECTION] ?? '');
            const originalTargetMedia = toTargetMediaValue(article?.[editableFields.TARGET_MEDIA]);
            const originalPaid = normalizeArticlePaidValue(article?.[editableFields.PAID]);
            const originalAiClause = Boolean(article?.[editableFields.AI_CLAUSE]);

            if (
                typeof parsed?.[editableFields.HEADER] === 'string' &&
                parsed[editableFields.HEADER] !== originalHeader
            ) {
                next[editableFields.HEADER] = parsed[editableFields.HEADER];
            }
            if (
                typeof parsed?.[editableFields.INGRESS] === 'string' &&
                parsed[editableFields.INGRESS] !== originalIngress
            ) {
                next[editableFields.INGRESS] = parsed[editableFields.INGRESS];
            }
            if (
                typeof parsed?.[editableFields.BODY] === 'string' &&
                parsed[editableFields.BODY] !== originalBody
            ) {
                next[editableFields.BODY] = parsed[editableFields.BODY];
            }
            if (
                typeof parsed?.[editableFields.AUTHOR] === 'string' &&
                parsed[editableFields.AUTHOR] !== originalAuthor
            ) {
                next[editableFields.AUTHOR] = parsed[editableFields.AUTHOR];
            }
            if (
                typeof parsed?.[editableFields.PHOTOGRAPHER] === 'string' &&
                parsed[editableFields.PHOTOGRAPHER] !== originalPhotographer
            ) {
                next[editableFields.PHOTOGRAPHER] = parsed[editableFields.PHOTOGRAPHER];
            }
            if (
                typeof parsed?.[editableFields.CAPTION] === 'string' &&
                parsed[editableFields.CAPTION] !== originalCaption
            ) {
                next[editableFields.CAPTION] = parsed[editableFields.CAPTION];
            }
            if (
                Object.prototype.hasOwnProperty.call(parsed ?? {}, editableFields.IMAGE_DATA) &&
                toNullableString(parsed?.[editableFields.IMAGE_DATA]) !== originalImageData
            ) {
                next[editableFields.IMAGE_DATA] = toNullableString(parsed?.[editableFields.IMAGE_DATA]);
            }
            if (
                typeof parsed?.[editableFields.SECTION] === 'string' &&
                parsed[editableFields.SECTION] !== originalSection
            ) {
                next[editableFields.SECTION] = parsed[editableFields.SECTION];
            }
            if (
                typeof parsed?.[editableFields.TARGET_MEDIA] === 'string' &&
                parsed[editableFields.TARGET_MEDIA] !== originalTargetMedia
            ) {
                next[editableFields.TARGET_MEDIA] = parsed[editableFields.TARGET_MEDIA];
            }
            if (parsed?.[editableFields.PAID] != null) {
                const parsedPaid = normalizeArticlePaidValue(parsed[editableFields.PAID]);
                if (parsedPaid !== originalPaid) {
                    next[editableFields.PAID] = parsedPaid;
                }
            }
            if (
                typeof parsed?.[editableFields.AI_CLAUSE] === 'boolean' &&
                parsed[editableFields.AI_CLAUSE] !== originalAiClause
            ) {
                next[editableFields.AI_CLAUSE] = parsed[editableFields.AI_CLAUSE];
            }
            if (
                Array.isArray(parsed?.[editableFields.MULTI_PUBLICATION_TARGET_MEDIAS])
            ) {
                next[editableFields.MULTI_PUBLICATION_TARGET_MEDIAS] = parsed[editableFields.MULTI_PUBLICATION_TARGET_MEDIAS];
            }

            if (Object.keys(next).length === 0) {
                localStorage.removeItem(getArticleEditsStorageKey(articleId));
            } else {
                localStorage.setItem(getArticleEditsStorageKey(articleId), JSON.stringify(next));
            }

            setSavedEdits(next);
            setEditingField(null);
            setEditingValue('');
        } catch {
            setSavedEdits({});
            setEditingField(null);
            setEditingValue('');
        }
    }, [
        articleId,
        article?.article_header,
        article?.article_ingress,
        article?.article_body,
        article?.article_author,
        article?.photographer,
        article?.caption,
        article?.image_data,
        article?.article_section,
        article?.target_media,
        article?.article_paid,
        article?.ai_clause,
        article?.multi_publication_target_medias,
        editableFields,
    ]);

    const persistSavedEdits = useCallback(
        (next, notifyPersist = true) => {
            setSavedEdits(next);
            if (!articleId) return;

            try {
                if (Object.keys(next).length === 0) {
                    localStorage.removeItem(getArticleEditsStorageKey(articleId));
                    window.dispatchEvent(
                        new CustomEvent('newsQueue:articleEditsChanged', {
                            detail: { articleId },
                        })
                    );
                    return;
                }

                localStorage.setItem(getArticleEditsStorageKey(articleId), JSON.stringify(next));
                window.dispatchEvent(
                    new CustomEvent('newsQueue:articleEditsChanged', {
                        detail: { articleId },
                    })
                );
            } catch {
                // ignore storage errors
            }

            if (notifyPersist && onPersistEdits) {
                onPersistEdits(next);
            }
        },
        [articleId, onPersistEdits]
    );

    const setTextFields = useCallback(
        (changes) => {
            if (!canEditFields) return;
            if (!changes || typeof changes !== 'object') return;

            const next = { ...savedEdits };

            Object.entries(changes).forEach(([field, value]) => {
                const nextValue = String(value ?? '');
                const originalValue =
                    field === editableFields.TARGET_MEDIA
                        ? toTargetMediaValue(article?.[field])
                        : field === editableFields.PAID
                            ? normalizeArticlePaidValue(article?.[field])
                            : String(article?.[field] ?? '');

                if (nextValue === originalValue) {
                    delete next[field];
                } else {
                    next[field] = nextValue;
                }
            });

            persistSavedEdits(next);
        },
        [
            article,
            canEditFields,
            editableFields.PAID,
            editableFields.TARGET_MEDIA,
            persistSavedEdits,
            savedEdits,
        ]
    );

    const setNullableField = useCallback(
        (field, value) => {
            if (!canEditFields) return;

            const originalValue = toNullableString(article?.[field]);
            const nextValue = toNullableString(value);
            const next = { ...savedEdits };

            if (nextValue === originalValue) {
                delete next[field];
            } else {
                next[field] = nextValue;
            }

            persistSavedEdits(next);
        },
        [article, canEditFields, persistSavedEdits, savedEdits]
    );

    const setTextField = useCallback(
        (field, value) => {
            setTextFields({ [field]: value });
        },
        [setTextFields]
    );

    const setArrayField = useCallback(
        (field, value) => {
            if (!canEditFields) return;
            if (field !== editableFields.MULTI_PUBLICATION_TARGET_MEDIAS) return;

            // Keep explicit empty selections too, so configuration defaults do not reappear.
            const next = { ...savedEdits, [field]: toArrayValue(value) };
            persistSavedEdits(next, false);
        },
        [canEditFields, editableFields.MULTI_PUBLICATION_TARGET_MEDIAS, persistSavedEdits, savedEdits]
    );

    const getEffectiveFieldValue = useCallback(
        (field) => {
            if (typeof savedEdits[field] === 'string') return savedEdits[field];
            if (field === editableFields.TARGET_MEDIA) {
                return toTargetMediaValue(article?.[field]);
            }
            if (field === editableFields.PAID) {
                return normalizeArticlePaidValue(article?.[field]);
            }
            if (field === editableFields.MULTI_PUBLICATION_TARGET_MEDIAS) {
                if (Array.isArray(savedEdits[field])) {
                    return savedEdits[field];
                }
                return toArrayValue(article?.[field]);
            }
            return String(article?.[field] ?? '');
        },
        [article, editableFields.PAID, editableFields.TARGET_MEDIA, editableFields.MULTI_PUBLICATION_TARGET_MEDIAS, savedEdits]
    );

    const getEffectiveBooleanFieldValue = useCallback(
        (field) => {
            if (typeof savedEdits[field] === 'boolean') return savedEdits[field];
            return Boolean(article?.[field]);
        },
        [article, savedEdits]
    );

    const getEffectiveNullableFieldValue = useCallback(
        (field) => {
            if (Object.prototype.hasOwnProperty.call(savedEdits, field)) {
                return toNullableString(savedEdits[field]);
            }
            return toNullableString(article?.[field]);
        },
        [article, savedEdits]
    );

    const hasSavedEdit = useCallback(
        (field) => Object.prototype.hasOwnProperty.call(savedEdits, field),
        [savedEdits]
    );

    const beginEdit = useCallback(
        (field) => {
            if (!canEditFields) return;
            const initialValue = getEffectiveFieldValue(field);
            editingDraftRef.current = initialValue;
            setEditingField(field);
            setEditingValue(initialValue);
        },
        [canEditFields, getEffectiveFieldValue]
    );

    const cancelEdit = useCallback(() => {
        setEditingField(null);
        setEditingValue('');
        editingDraftRef.current = '';
    }, []);

    const applyEdit = useCallback(() => {
        if (!editingField || !canEditFields) return;
        const originalValue = String(article?.[editingField] ?? '');
        const nextValue = editingDraftRef.current;
        const next = { ...savedEdits };

        if (nextValue === originalValue) {
            delete next[editingField];
        } else {
            next[editingField] = nextValue;
        }

        persistSavedEdits(next);
        setEditingField(null);
        setEditingValue('');
        editingDraftRef.current = '';
    }, [article, canEditFields, editingField, persistSavedEdits, savedEdits]);

    const revertSavedField = useCallback(
        (field) => {
            if (!canEditFields) return;
            const next = { ...savedEdits };
            delete next[field];
            persistSavedEdits(next);
        },
        [canEditFields, persistSavedEdits, savedEdits]
    );

    const setBooleanField = useCallback(
        (field, checked) => {
            if (!canEditFields) return;

            const originalValue = Boolean(article?.[field]);
            const next = { ...savedEdits };
            if (checked === originalValue) {
                delete next[field];
            } else {
                next[field] = checked;
            }
            persistSavedEdits(next);
        },
        [article, canEditFields, persistSavedEdits, savedEdits]
    );

    useEffect(() => {
        if (canEditFields) return;
        if (!editingField) return;
        setEditingField(null);
        setEditingValue('');
        editingDraftRef.current = '';
    }, [canEditFields, editingField]);

    const effectiveArticleHeader = getEffectiveFieldValue(editableFields.HEADER);
    const effectiveArticleIngress = getEffectiveFieldValue(editableFields.INGRESS);
    const effectiveArticleBody = getEffectiveFieldValue(editableFields.BODY);
    const effectiveArticleAuthor = getEffectiveFieldValue(editableFields.AUTHOR);
    const effectivePhotographer = getEffectiveFieldValue(editableFields.PHOTOGRAPHER);
    const effectiveCaption = getEffectiveFieldValue(editableFields.CAPTION);
    const effectiveImageData = getEffectiveNullableFieldValue(editableFields.IMAGE_DATA);
    const effectiveArticleSection = getEffectiveFieldValue(editableFields.SECTION);
    const effectiveTargetMedia = getEffectiveFieldValue(editableFields.TARGET_MEDIA);
    const effectiveArticlePaid = getEffectiveFieldValue(editableFields.PAID);
    const effectiveAiClause = getEffectiveBooleanFieldValue(editableFields.AI_CLAUSE);
    const effectiveMultiPublicationTargetMedias = getEffectiveFieldValue(editableFields.MULTI_PUBLICATION_TARGET_MEDIAS);

    const articleForPublish = useMemo(() => {
        if (!article) return article;
        return {
            ...article,
            article_header: effectiveArticleHeader,
            article_ingress: effectiveArticleIngress,
            article_body: effectiveArticleBody,
            article_author: effectiveArticleAuthor,
            photographer: effectivePhotographer,
            caption: effectiveCaption,
            image_data: effectiveImageData,
            article_section: effectiveArticleSection,
            target_media: effectiveTargetMedia,
            article_paid: effectiveArticlePaid,
            ai_clause: effectiveAiClause,
            multi_publication_target_medias: effectiveMultiPublicationTargetMedias,
        };
    }, [
        article,
        effectiveAiClause,
        effectiveArticleAuthor,
        effectiveArticleBody,
        effectivePhotographer,
        effectiveCaption,
        effectiveImageData,
        effectiveArticleSection,
        effectiveArticleHeader,
        effectiveArticleIngress,
        effectiveTargetMedia,
        effectiveArticlePaid,
        effectiveMultiPublicationTargetMedias,
    ]);

    return {
        editingField,
        editingValue,
        setEditingValue,
        editingDraftRef,
        setTextField,
        setTextFields,
        setNullableField,
        setArrayField,
        hasSavedEdit,
        beginEdit,
        cancelEdit,
        applyEdit,
        revertSavedField,
        setBooleanField,
        effectiveArticleHeader,
        effectiveArticleIngress,
        effectiveArticleBody,
        effectiveArticleAuthor,
        effectivePhotographer,
        effectiveCaption,
        effectiveImageData,
        effectiveArticleSection,
        effectiveTargetMedia,
        effectiveArticlePaid,
        effectiveAiClause,
        effectiveMultiPublicationTargetMedias,
        articleForPublish,
    };
}
